import { useState } from "react";
import classes from "../homePageNew.module.css";
import ScoreDonut from "./ScoreDonut";
import MetricGrid from "./MetricGrid";
import KeyInsightsPanel from "./KeyInsightsPanel";
import LlmScoreTable from "./LlmScoreTable";
import CombinationSelector from "./CombinationSelector";
import ReportStrip from "./ReportStrip";
import FailedRunPanel from "./FailedRunPanel";
import EmptyRecordState from "./EmptyRecordState";
import SectionEmptyState from "./SectionEmptyState";
import ShareModal from "./ShareModal";
import { CategoryIcon, ShareIcon } from "./icons";
import BrandAvatar from "../../../Component/Avatar/Avatar";
import { pillClass, resolveMetrics, normalizeScoreKeys, matchesCombination } from "../api/homePageApi";

const DIMENSIONS = [
  { key: "category", label: "Category" },
  { key: "market", label: "Market" },
  { key: "persona", label: "Persona" },
];

/**
 * Brand Audit's own detail pane — a Brand Audit record can carry several
 * category/market/persona/llm combinations at once (Discovery only ever has
 * one), so this adds a combination picker (category/market/persona — not
 * LLM, which is shown as a table of every model instead of picked one at a
 * time) above the same generic donut/metrics/insights layout
 * ModuleDetailPane.js uses for Discovery.
 */
export default function BrandAuditDetailPane({
  record,
  moduleLabel,
  noDataAtAll,
  moduleKey,
  combination,
  onCombinationChange,
  onRetried,
  onViewFull,
  fullViewUrl,
  userId,
  onOpenReportsModal,
  onViewReport,
}) {
  const [shareOpen, setShareOpen] = useState(false);

  if (!record) {
    return (
      <div className={classes.mdDetailPane}>
        <EmptyRecordState moduleLabel={moduleLabel} noDataAtAll={noDataAtAll} />
      </div>
    );
  }

  const label = record.brand || record.category || "Untitled";
  const status = pillClass(record.status);

  if (status === "failed") {
    return (
      <div className={classes.mdDetailPane}>
        <FailedRunPanel transactionId={record.transactionId} failReason={record.failReason} onRetried={onRetried} />
      </div>
    );
  }

  const entries = Array.isArray(record.metrics) ? record.metrics : [];
  const combinations = entries.map((e) => e.combination).filter(Boolean);

  // The picker only covers category/market/persona; the effective category/
  // market/persona (defaulting to the first combination) is resolved once
  // and reused both for the top summary (paired with primaryLLM) and for
  // which rows the LLM table shows (every LLM at that same combination).
  const effective = {
    category: combination?.category ?? combinations[0]?.category ?? null,
    market: combination?.market ?? combinations[0]?.market ?? null,
    persona: combination?.persona ?? combinations[0]?.persona ?? null,
  };

  const { scores: resolvedScores, insights } = resolveMetrics(record, { ...effective, llm: record.primaryLLM });
  const scores = resolvedScores || {};
  const dataUnavailable = !resolvedScores;
  const isProduct = String(record.type || "").toLowerCase() === "product";

  const llmRows = entries
    .filter((e) => matchesCombination(e.combination, effective))
    .map((e) => ({ llm: e.combination?.llm, scores: normalizeScoreKeys(e.scores) }))
    .filter((row) => row.llm);

  const subtitleParts = [effective.category, effective.market, effective.persona].filter(Boolean);

  return (
    <div className={classes.mdDetailPane}>
      <div className={classes.detailHeader}>
        <BrandAvatar domainName={record.brandDomain} brandName={label} />
        <div className={classes.detailHeaderText}>
          <div className={classes.detailHeaderName}>{label}</div>
          <div className={classes.detailHeaderSubtitle}>{subtitleParts.join(" · ")}</div>
        </div>
      </div>

      {combinations.length > 1 && (
        <CombinationSelector combinations={combinations} value={effective} onChange={onCombinationChange} dimensions={DIMENSIONS} />
      )}

      {dataUnavailable ? (
        <SectionEmptyState
          icon={CategoryIcon}
          title="Full scores aren't available yet"
          description="This run hasn't finished processing — full detail will show here once it's ready."
        />
      ) : (
        <>
          <div className={classes.mainRow}>
            <div className={classes.mainRowLeft}>
              <ScoreDonut
                visibility={scores.visibility_score}
                sentiment={scores.sentiment_score}
                brandPillars={scores.brand_pillars_score}
                purchaseCriteria={scores.purchase_criteria_score}
                brandIndexScore={scores.brand_index_score}
              />
              <MetricGrid
                visibility={scores.visibility_score}
                sentiment={scores.sentiment_score}
                brandPillars={scores.brand_pillars_score}
                purchaseCriteria={scores.purchase_criteria_score}
                isProduct={isProduct}
              />
            </div>
            <div className={classes.mainRowRight}>
              <KeyInsightsPanel insights={insights} />
            </div>
          </div>
          <LlmScoreTable rows={llmRows} primaryLLM={record.primaryLLM} isProduct={isProduct} />
        </>
      )}

      <div className={classes.detailActionsRow}>
        <button
          className={classes.viewBtn}
          onClick={() => onViewFull(record)}
          disabled={dataUnavailable && status !== "inProgress"}
          title={dataUnavailable && status !== "inProgress" ? "Full detail isn't available yet for this run" : undefined}
        >
          View {moduleLabel} →
        </button>
        {fullViewUrl && (
          <button
            className={classes.drawerActionBtn}
            onClick={() => setShareOpen(true)}
            disabled={status === "inProgress"}
            title={status === "inProgress" ? "Sharing isn't available until the run finishes" : "Share"}
          >
            <ShareIcon size={14} />
            Share
          </button>
        )}
      </div>
      <ReportStrip
        report={record.report}
        unavailable={dataUnavailable}
        onOpenReportsModal={() => onOpenReportsModal(record)}
        onViewReport={onViewReport}
        moduleKey={moduleKey}
      />
      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        itemName={`${label} ${moduleLabel}`}
        documentPath={fullViewUrl ? `${window.location.origin}${fullViewUrl}` : ""}
        transactionId={record.transactionId}
        userId={userId}
      />
    </div>
  );
}
